import { request, ResponseError } from "@utils/request"

import { analytics } from "../utils/analytics"

export const HTTP_STATUS_FORBIDDEN = 403

// "NoAccess" 403 means the user has no session and must SSO in.
// Any other error (including expired-token 403s) deliberately bubbles to the
// error UI instead of triggering a re-login — this behavior is unchanged by the
// auth refactor essentials - only the mechanism (query-driven vs. auth provider) changed.
export const isLoggedOutError = (error: unknown): boolean =>
  error instanceof ResponseError &&
  error.statusCode === HTTP_STATUS_FORBIDDEN &&
  error.title === "NoAccess"

export const handleRefresh = async (): Promise<void> => {
  try {
    await request.post("auth/refresh", { hooks: { afterResponse: undefined } })
  } catch (error) {
    // oxlint-disable-next-line no-console
    console.error(error)
  }
}

/**
 * Starts the SSO login flow (redirects to the IdP). Used both for an initial
 * login and to retry after an auth error.
 */
export const startLogin = async (): Promise<never> => {
  analytics.event("auth__login-started")
  const { redirectUri } = await request
    .post("auth/login?api-version=1", {
      json: { redirectUri: "https://app.dovetail.puzzel.com/" },
    })
    .json<{ redirectUri: string }>()
  window.location.href = redirectUri
  // Page is navigating away. Block forever so callers cannot proceed
  // (otherwise `_authenticated.beforeLoad` would re-throw and briefly render the
  // error UI before the redirect completes). `Promise.race([])` never
  // settles because nothing can win an empty race.
  return Promise.race([])
}

/**
 * Logs out via the IdP. The backend returns a `redirectUri` that continues the
 * logout flow on the Identity Provider and lands the user back at the SSO
 * screen. In local dev the IdP redirect won't resolve, but in production it
 * ends at `https://copilot.puzzel.com/`.
 */
export const handleLogout = async (): Promise<void> => {
  const { redirectUri } = await request
    .post("auth/logout?api-version=1")
    .json<{ redirectUri: string }>()
  window.location.href = redirectUri
}
